import { useEffect, useRef, useState, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";

import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import intransitInvService from "@plannel/services/data/intransit-inv-service";
import locationService from "@plannel/services/data/location-service";
import customerService from "@plannel/services/data/customer-service";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import PaginationContainer from "@plannel/components/PaginationContainer";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";
import DateCondition from "@plannel/components/DateCondition";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import GridDatePicker from "@plannel/components/GridDatePicker";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const IntransitInventoryMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxNotifyState = reduxUtil.getViewState(`${viewName}_NOTIFY`);
  const reduxDispatch = useDispatch();
  const showLimitSnackbar = useRef(reduxNotifyState ? reduxNotifyState?.showLimitSnackbar : true);

  const pageSizes = [100, 200, 500];
  const [resultTotalCount, setResultTotalCount] = useState(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? { originLocation: null, destLocation: null });
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [limitInfo, setLimitInfo] = useState({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [intransitTxn, setIntransitTxn] = useState([]);
  const [locations, setLocations] = useState([]);
  const [customers, setCustomers] = useState([]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { dataEmpty, saveSuccess, saveFail, noSaveData, exportLimit } = ColorUtil.snackBarMessageColor;
  const { intransitCondition } = CodeUtil;

  const CURRENT_DATE = new ZDate();
  const MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
  const MAX_DATE = new ZDate(CURRENT_DATE.getFullYear() + 10, 0, 1);
  const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
  const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 3, 0);
  const initPeriod = { start: reduxViewState?.period ? new ZDate(reduxViewState.period.start) : START_DATE, end: reduxViewState?.period ? new ZDate(reduxViewState.period.end) : END_DATE };
  const [period, setPeriod] = useState(initPeriod);
  const [selectedSearch, setSelectedSearch] = useState(reduxViewState?.selectedSearch ?? "eta");

  const dropdownValueFormatter = params => {
    return params?.value?.name ? params.value.name : "";
  };

  const objectComparator = (value1, value2, isDescending) => {
    const val1 = !value1 ? "" : value1.name;
    const val2 = !value2 ? "" : value2.name;
    
    return nullSafeComparator(val1, val2, isDescending);
  };

  const columnDefs = [
    {
      headerName: "originLocation",
      field: "originLocation",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: dropdownValueFormatter,
      cellEditorParams: {
        values: locations,
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, isDescending),
      width: 130, 
      filterType: "string",
    },
    {
      headerName: "destLocation",
      field: "destLocation",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: dropdownValueFormatter,
      cellEditorParams: {
        values: locations,
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, isDescending),
      width: 130, 
      filterType: "string",
    },
    { headerName: "customerCd", field: "customer.customerCd", colId: "customerCd", width: 130, type: ["nonEditableColumn"], cellClass: "stringType", filterType: 'string' },
    {
      headerName: "customerName",
      field: "customer",
      colId: "customerName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: dropdownValueFormatter,
      cellEditorParams: {
        values: customers,
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, isDescending),
      minWidth: 130, 
      filterType: "string",
    },
    // { headerName: "categoryName", field: "categoryName", type: ["nonEditableColumn"], width: 150 },
    { headerName: "itemCd", field: "itemCd", width: 150, cellClass: "stringType", filterType: "string" },
    { headerName: "itemName", field: "itemNm", type: ["nonEditableColumn"], width: 150, filterType: "string" },
    { headerName: "baseDt", field: "baseDate", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "invCd", field: "invCd", width: 150, cellClass: "stringType", filterType: "string" },
    { headerName: "etd", field: "etd", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "atd", field: "atd", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "eta", field: "eta", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "expDate", field: "expDate", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "orderDate", field: "orderDate", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "orderQty", field: "orderQty", type: ["numberColumn", "rightAligned"], width: 130, cellEditor: NumericEditor, filterType: "number" },
    { headerName: "shipmentQty", field: "shptQty", type: ["numberColumn", "rightAligned"], width: 130, cellEditor: NumericEditor, filterType: "number" },
    { headerName: "orderNum", field: "orderNum", width: 150, filterType: "string" },
    { headerName: "invoiceNum", field: "invoiceNum", width: 150, filterType: "string" },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: "string" },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: "string" },
  ];

  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = {};

    if (locationSearch?.originLocation) {
      searchFilters.originLocCd = locationSearch.originLocation.locCd;
    }

    if (locationSearch?.destLocation) {
      searchFilters.destLocCd = locationSearch.destLocation.locCd;
    }

    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    } else if (customerSearch.current) {
      searchFilters.customer = customerSearch.current;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (selectedSearch) {
      searchFilters.condition = selectedSearch;
    }

    if (period?.start) {
      searchFilters.startDate = period?.start;
    }

    if (period?.end) {
      searchFilters.endDate = period?.end;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  };

  const retrieveIntransitTxn = () => {
    const params = getRequestParams();
    intransitInvService
      .getAll(params)
      .then(response => {
        const { results, totalPages, totalItems, isLimit, limitSize } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: dataEmpty, content: t("MSG_NoData") });
        } else {
          setLimitInfo({ isLimit, limitSize });
        }

        setIntransitTxn(results);
        setTotalPages(totalPages);
        setResultTotalCount(totalItems);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxObject = { period, selectedSearch, locationSearch, customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize, advancedFilters };
        const reduxState = reduxUtil.createObjectState(viewName, reduxObject);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  };

  const retrieveLocation = () => {
    locationService.getAll({ pagination: false })
      .then(response => {
        const { data } = response;
        const { results } = data;

        setLocations(results);
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveCustomer = () => {
    customerService
      .getAll()
      .then(response => {
        const { results } = response.data;
        const filteredResults = results.filter(result => {
          return result.activeFlg === true && result.delFlg === false;
        });

        setCustomers(filteredResults);
      })
      .catch(e => {
        debug(e);
      });
  };

  const onGridReady = params => {
    retrieveLocation();
    retrieveCustomer();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const removeIntransitTxn = () => {
    const intransitTxnIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(data => {
      intransitTxnIds.push(data.id);
    });

    if (intransitTxnIds.length > 0) {
      intransitInvService
        .remove(intransitTxnIds)
        .then(() => {
          setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .finally(() => {
          retrieveIntransitTxn();
        });
    } else {
      setSnackInfo({ open: true, severity: noSaveData, content: t("MSG_NoSelectedData") });
    }
  };

  const validationRowData = allStateData => {
    for (const rowData of allStateData) {
      if (!rowData.baseDate) {
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_NoBaseDate") });
        return false;
      } else if (!rowData.invCd) {
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_NoInvCd") });
        return false;
      } else if (!rowData.itemCd) {
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_NoItemCd") });
        return false;
      } else if (!rowData.shptQty) {
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_NoShipQty") });
        return false;
      }
    }
    return true;
  };

  const saveIntransitTxn = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      const isCheck = validationRowData(allStateData);
      if (isCheck) {
        intransitInvService
          .upsert(allStateData)
          .then(() => {
            setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
          })
          .catch(e => {
            const { message } = e.response?.data;
            if (message) {
              setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
            } else {
              setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
            }
            debug(e);
          })
          .finally(() => {
            retrieveIntransitTxn();
          });
      }
    } else {
      setSnackInfo({ open: true, severity: noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const handleDialog = action => {
    if (action === "SAVE") {
      saveIntransitTxn();
    } else if (action === "DELETE") {
      removeIntransitTxn();
    }
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveIntransitTxn();
    }
  };

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveIntransitTxn();
    }

    return () => {
      setIntransitTxn([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setIntransitTxn([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, locationSearch.originLocation, locationSearch.destLocation, period, selectedSearch]);

  useEffect(() => {
    if (limitInfo.isLimit && showLimitSnackbar.current) {
      setSnackInfo({ open: true, severity: exportLimit, content: `${t("MSG_LimitedSearchResults", { N: limitInfo.limitSize })}` });
      reduxDispatch(reduxUtil.updateViewState({ viewName: `${viewName}_NOTIFY`, showLimitSnackbar: false }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitInfo.isLimit, showLimitSnackbar.current]);

  useEffect(() => {
    if (!isEmpty(locations) || !isEmpty(customers)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, customers]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const addRow = () => {
    const newRow = {
      id: null,
      originLocation: null,
      destLocation: null,
      avgLtDays: 0,
      verNum: 0,
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <DateCondition
          conditionList={intransitCondition}
          period={period}
          maxDate={MAX_DATE}
          minDate={MIN_DATE}
          selectedSearch={selectedSearch}
          setSelectedSearch={setSelectedSearch}
          setPeriod={setPeriod}
          useAutoComplete={false}
          views={['year', 'month']}
        />
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch.originLocation}
          onChange={(_, newValue) => setLocationSearch({ ...locationSearch, originLocation: newValue })}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ minWidth: 240, mr: 1 }}
          renderInput={params => (
            <TextField
              {...params}
              label={t("originLocation")}
            />
          )}
        />
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch.destLocation}
          onChange={(_, newValue) => setLocationSearch({ ...locationSearch, destLocation: newValue })}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ mr: 1, minWidth: 240 }}
          renderInput={params => (
            <TextField
              {...params}
              label={t("destLocation")}
            />
            )}
        />
        <CustomerAutocomplete
          retrieve={initPageToRetrieve}
          searchValue={customerSearch}
          useAccessFilter={false}
          sx={{ width: "240px" , mr: 1 }}
          extraCodesRef={extraCustomerCodesRef}
        />
        <ItemAutocomplete
          retrieve={initPageToRetrieve}
          searchValue={itemSearch}
          sx={{ width: "240px", mr: 1 }}
          extraCodesRef={extraItemCodesRef}
        />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title}
            service={intransitInvService}
            columnDefs={columnDefs}
            totalRows={resultTotalCount}
            params={getRequestParams(false)}
          />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={intransitTxn}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              statusBar={statusBar}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage}
        totalPages={tPages}
        pageSize={pageSize}
        pageSizes={pageSizes}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        disableButton={dialogInfo.disableButton}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(IntransitInventoryMaster);
